import TomSelect from "tom-select"

export interface EnhancedSelectOptionData {
  $option?: HTMLOptionElement
  text?: string
  value?: string
}

export interface EnhancedSelectItem {
  [key: string]: unknown
}

export type RenderFunction = (
  data: EnhancedSelectOptionData,
  escape: (str: string) => string
) => string

export type CreateRenderFunction = (
  data: { input: string },
  escape: (str: string) => string
) => string

export type NoResultsRenderFunction = () => string

export type OptgroupHeaderRenderFunction = (
  data: { value: string; label: string },
  escape: (str: string) => string
) => string

export interface OptgroupConfig {
  value: string
  label: string
}

export interface EnhancedSelectOptions {
  placeholder?: string
  allowClear?: boolean
  allowRemove?: boolean
  closeOnSelect?: boolean
  openOnFocus?: boolean
  maxItems?: number | null
  dropdownParent?: HTMLElement
  onChange?: (value: string | string[]) => void
  onItemAdd?: (value: string) => void
  onItemRemove?: (value: string) => void
  onClear?: () => void
  render?: {
    option?: RenderFunction
    item?: RenderFunction
    option_create?: CreateRenderFunction
    no_results?: NoResultsRenderFunction
    optgroup_header?: OptgroupHeaderRenderFunction
  }
  create?: boolean | ((input: string) => { value: string; text: string } | false)
  createFilter?: RegExp | string | ((input: string) => boolean)
  hideFromSearch?: (value: string) => boolean
  disableSearchFiltering?: boolean
  synchronousRefresh?: boolean
  highlightMatches?: boolean

  // AJAX and optgroup options
  optgroups?: OptgroupConfig[]
  optgroupField?: string
  valueField?: string
  labelField?: string
  /**
   * Async function to load options from a remote source.
   * Returns a Promise that resolves to an array of items.
   * EnhancedSelect handles all abort/cancel logic internally -
   * consumers don't need to catch AbortError or check signal.aborted.
   */
  load?: (query: string, signal: AbortSignal) => Promise<unknown[]>
  loadThrottle?: number
  shouldLoad?: (query: string) => boolean
  preload?: boolean | "focus"
  disableQueryCache?: boolean

  // Dropdown event callbacks
  onDropdownOpen?: () => void
  onFocus?: () => void

  // Plugin options
  dropdownInput?: boolean // Enable dropdown_input plugin for searchable single-selects
}

export class EnhancedSelect {
  private tomSelect: TomSelect
  private element: HTMLSelectElement
  private loadAbortController: AbortController | null = null

  constructor(element: HTMLSelectElement, options: EnhancedSelectOptions = {}) {
    this.element = element

    const plugins: Record<string, object> = {}

    if (options.allowClear !== false) {
      plugins.clear_button = { title: "Clear selection" }
    }

    const isMultiple = element.multiple || (options.maxItems !== undefined && options.maxItems !== 1)
    if (isMultiple && options.allowRemove !== false) {
      plugins.remove_button = { title: "Remove" }
    }

    if (options.dropdownInput) {
      plugins.dropdown_input = {}
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tomSelectOptions: any = {
      placeholder: options.placeholder || "",
      plugins: plugins,
      closeAfterSelect: options.closeOnSelect ?? true,
      openOnFocus: options.openOnFocus ?? true,
      maxItems: options.maxItems !== undefined ? options.maxItems : (element.multiple ? null : 1),
    }

    if (options.dropdownParent) {
      tomSelectOptions.dropdownParent = options.dropdownParent
    }

    if (options.onChange) {
      tomSelectOptions.onChange = options.onChange
    }

    // When closeOnSelect is enabled (default) with AJAX loading, blur after item add
    // to prevent late AJAX callbacks from reopening the dropdown.
    // See: https://github.com/orchidjs/tom-select/issues/963
    const shouldBlurAfterSelect = options.closeOnSelect !== false && options.load
    if (options.onItemAdd || shouldBlurAfterSelect || options.load) {
      tomSelectOptions.onItemAdd = (value: string | number) => {
        this.loadAbortController?.abort()
        this.loadAbortController = null

        options.onItemAdd?.(String(value))
        if (shouldBlurAfterSelect) {
          this.tomSelect?.blur()
        }
      }
    }

    if (options.onItemRemove) {
      tomSelectOptions.onItemRemove = (value: string | number) => {
        options.onItemRemove?.(String(value))
      }
    }

    if (options.onClear) {
      tomSelectOptions.onClear = options.onClear
    }

    if (options.render) {
      tomSelectOptions.render = options.render
    }

    if (options.create) {
      tomSelectOptions.create = options.create
      tomSelectOptions.createOnBlur = false
    }

    if (options.createFilter) {
      tomSelectOptions.createFilter = options.createFilter
    }

    if (options.hideFromSearch) {
      const hideFromSearch = options.hideFromSearch
      tomSelectOptions.score = () => {
        return (item: { value: string }) => {
          return hideFromSearch(item.value) ? 0 : 1
        }
      }
    }

    if (options.disableSearchFiltering) {
      tomSelectOptions.searchField = []
    }

    if (options.synchronousRefresh) {
      tomSelectOptions.refreshThrottle = 0
    }

    if (options.highlightMatches !== undefined) {
      tomSelectOptions.highlight = options.highlightMatches
    }

    // Optgroup configuration
    if (options.optgroups) {
      tomSelectOptions.optgroups = options.optgroups
      tomSelectOptions.optgroupField = options.optgroupField || "optgroup"
      tomSelectOptions.lockOptgroupOrder = true
    }

    // Custom field mapping for AJAX data
    if (options.valueField) {
      tomSelectOptions.valueField = options.valueField
    }

    if (options.labelField) {
      tomSelectOptions.labelField = options.labelField
    }

    // AJAX loading with automatic request cancellation
    if (options.load) {
      const userLoad = options.load
      let pendingLoadCallback: ((items: unknown[]) => void) | null = null

      tomSelectOptions.load = (query: string, callback: (items: unknown[]) => void) => {
        // TomSelect's `loading` counter only decrements when a callback fires,
        // so a skipped (aborted or stale-query) callback pins the spinner open.
        // Resolve every superseded callback with [] to keep accounting sound.
        this.loadAbortController?.abort()
        pendingLoadCallback?.([])

        this.loadAbortController = new AbortController()
        pendingLoadCallback = callback
        const signal = this.loadAbortController.signal

        this.tomSelect.clearOptions()

        const resolveLoad = (items: unknown[]) => {
          if (pendingLoadCallback !== callback) return
          pendingLoadCallback = null
          callback(items)
        }

        userLoad(query, signal)
          .then((items) => {
            if (signal.aborted) return
            const currentQuery = this.tomSelect.control_input.value
            resolveLoad(query === currentQuery ? items : [])
          })
          .catch((error) => {
            if (error?.name === "AbortError" || error?.name === "CanceledError") {
              return
            }
            resolveLoad([])
            throw error
          })
      }

      tomSelectOptions.loadThrottle = options.loadThrottle ?? 300

      if (options.shouldLoad) {
        tomSelectOptions.shouldLoad = options.shouldLoad
      }

      if (options.preload !== undefined) {
        tomSelectOptions.preload = options.preload
      }
    }

    // Dropdown event callbacks
    if (options.onDropdownOpen) {
      tomSelectOptions.onDropdownOpen = options.onDropdownOpen
    }

    if (options.onFocus) {
      tomSelectOptions.onFocus = options.onFocus
    }

    this.tomSelect = new TomSelect(element, tomSelectOptions)

    // Extend the render chain to make remove buttons screen-reader-friendly.
    // The remove_button plugin sets a static title="Remove" on every button.
    // We wrap render.item to update it to "Remove {item label}" per item.
    if (isMultiple && options.allowRemove !== false) {
      const origRenderItem = this.tomSelect.settings.render.item
      this.tomSelect.settings.render.item = (data: Record<string, unknown>, escape: (str: string) => string) => {
        const item = origRenderItem.call(this.tomSelect, data, escape) as HTMLElement
        const removeLink = item.querySelector('a[title="Remove"]')
        if (removeLink) {
          const label = String(data[this.tomSelect.settings.labelField] || data.text || "")
          removeLink.setAttribute("title", `Remove ${label}`)
        }
        return item
      }
    }

    // Mark the control container as a group so tests (and screen readers) can
    // locate the selected-items area via an accessible role instead of CSS classes.
    const associatedLabel = document.querySelector(`label[for="${this.tomSelect.control_input.id}"]`)
    if (associatedLabel) {
      this.tomSelect.control.setAttribute("role", "group")
      this.tomSelect.control.setAttribute("aria-label", associatedLabel.textContent?.trim() || "")
    }

    // Announce create capability to screen readers
    if (options.create) {
      this.tomSelect.control_input.setAttribute(
        "aria-description",
        "Type to search or create a new option"
      )
    }

    // Bypass TomSelect's query cache so every keystroke triggers a fresh load.
    // By default, TomSelect caches which queries have been loaded and won't
    // re-fetch for the same or "refined" queries (e.g., "test" covers "testing").
    // This is problematic for server-side search where each query needs fresh results.
    if (options.disableQueryCache) {
      this.tomSelect.hook("instead", "canLoad", () => !!this.tomSelect.settings.load)
    }
  }

  getValue(): string | string[] {
    return this.tomSelect.getValue()
  }

  setValue(value: string | string[], silent = true): void {
    this.tomSelect.setValue(value, silent)
  }

  clear(silent = true): void {
    this.tomSelect.clear(silent)
    // Also clear the underlying select element's selected options
    // This handles cases where TomSelect's internal items array is out of sync
    // with the actual HTML select element (e.g., when using Capybara's select helper)
    Array.from(this.element.options).forEach((option) => {
      option.selected = false
    })
  }

  addOption(data: Record<string, unknown>): void {
    this.tomSelect.addOption(data)
  }

  destroy(): void {
    this.loadAbortController?.abort()
    this.loadAbortController = null
    this.tomSelect.destroy()
  }

  updateOption(value: string, data: { value: string; text: string; disabled?: boolean }): void {
    this.tomSelect.updateOption(value, data)
  }

  getOption(value: string): HTMLElement | null {
    return this.tomSelect.getOption(value)
  }

  refreshOptions(triggerDropdown?: boolean): void {
    this.tomSelect.refreshOptions(triggerDropdown ?? false)
  }

  sync(): void {
    this.tomSelect.sync()
  }

  open(): void {
    this.tomSelect.open()
  }

  focus(): void {
    this.tomSelect.focus()
  }

  setTextboxValue(value: string): void {
    this.tomSelect.setTextboxValue(value)
    this.tomSelect.refreshOptions(true)
  }

  getDropdownContent(): HTMLElement {
    return this.tomSelect.dropdown_content
  }

  clearActiveOption(): void {
    this.tomSelect.setActiveOption(null)
  }

  /**
   * Returns the full data objects for all selected items.
   * Useful when you need access to custom properties beyond just the value.
   * Use a type parameter to specify the expected item shape.
   */
  getItems<T extends EnhancedSelectItem = EnhancedSelectItem>(): T[] {
    const items = this.tomSelect.items as string[]
    return items.map((id) => this.tomSelect.options[id] as T).filter(Boolean)
  }

  /**
   * Clears the dropdown options cache. Useful when using AJAX loading
   * and you want to force a fresh fetch.
   */
  clearOptions(): void {
    this.tomSelect.clearOptions()
  }

  /**
   * Resets the select to its initial state by clearing the selection
   * and removing all cached options. Useful for modals or forms that
   * need a fresh state on each open.
   */
  reset(): void {
    this.clear()
    this.clearOptions()
  }

  getControlInput(): HTMLInputElement {
    return this.tomSelect.control_input
  }
}
